/**
 * OPEN-157 — when the office cannot change a job's driver, as the server rules it
 * (api services/job-reassign-guard.ts). The server is the source of truth and answers 409
 * JOB_NOT_REASSIGNABLE; this mirror lets Job Detail show the driver read-only with the reason
 * instead of offering a change that will be refused, and words the refusal when one happens
 * anyway (a run ticket that landed after the board loaded).
 */

import { ApiError } from '@lodex-pro/web-shared';

export type DriverLock = 'started' | 'finished' | 'terminal' | null;

const FINISHED = new Set(['COMPLETED', 'IN_REVIEW', 'TO_BE_INVOICED']);
const TERMINAL = new Set(['CANCELLED', 'REJECTED', 'INVOICED']);

/** Why this job's driver can't change here, or null. Pure. */
export function driverLock(job: {
  readonly statusCode: string;
  readonly driverId: string | null;
  readonly hasCapture?: boolean;
  readonly captureVoidedAt?: string | null;
}): DriverLock {
  if (TERMINAL.has(job.statusCode)) return 'terminal';
  if (FINISHED.has(job.statusCode)) return 'finished';
  if (job.driverId === null) return null;
  const liveCapture = job.hasCapture === true && (job.captureVoidedAt ?? null) === null;
  return job.statusCode !== 'PENDING' || liveCapture ? 'started' : null;
}

export const DRIVER_LOCK_COPY: Readonly<Record<Exclude<DriverLock, null>, string>> = {
  started:
    "This job has started, so it stays with its driver. If they can't finish it, send it to review or cancel it.",
  finished: 'This ticket is finished, so it keeps the driver who hauled it.',
  terminal: 'This job is closed, so its driver is not changed.',
};

/** The line for a failed move or save; `fallback` when the error is not the reassignment refusal. */
export function reassignErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError) || err.code !== 'JOB_NOT_REASSIGNABLE') return fallback;
  const reason = err.fields[0]?.reason;
  return reason === 'job_finished' ? DRIVER_LOCK_COPY.finished : DRIVER_LOCK_COPY.started;
}
